import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, Pencil, Archive, ArchiveRestore, Info, Play, Sparkles, Loader2,
} from "lucide-react";
import SetupHealthCard from "@/components/SetupHealthCard";
import RuleEditorDialog from "@/components/RuleEditorDialog";
import RulePreviewDialog from "@/components/RulePreviewDialog";
import { useHashHighlight } from "@/lib/highlightRow";

// Assignment Rules & Compatibility.
// Captured for future automation. Strictly read/write, no consumption by
// rota/leave/swap/assignment write paths today.

const COMBINE_LABEL = {
  any: "No restriction",
  allowlist: "Allowlist",
  denylist:  "Denylist",
};
const COMBINE_TONE = {
  any: "slate", allowlist: "green", denylist: "red",
};

export default function AssignmentRulesPage() {
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [skills, setSkills] = useState([]);
  const [filterCat, setFilterCat] = useState("all");
  const [activeOnly, setActiveOnly] = useState(true);
  const [loading, setLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRule, setPreviewRule] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCat !== "all") params.set("category_id", filterCat);
      params.set("active_only", activeOnly ? "true" : "false");
      const [{ data: rs }, { data: cs }, { data: sk }] = await Promise.all([
        api.get(`/assignment-rules?${params.toString()}`),
        api.get("/categories"),
        api.get("/skills"),
      ]);
      setRules(rs);
      setCategories(cs);
      setSkills(sk.filter((s) => !s.is_archived));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to load rules");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCat, activeOnly]);

  // deep-link highlight from advisory popovers (#rule={id}).
  // If the linked rule is archived, auto-toggle off "active only" so the row
  // is visible. Read-only side-effect: only adjusts the local filter.
  useHashHighlight({
    key: "rule",
    testidPrefix: "rule-row-",
    deps: [rules],
    onMatch: (id) => {
      if (rules.length && !rules.find((r) => r.rule_id === id) && activeOnly) {
        setActiveOnly(false);
      }
    },
  });

  const sortedRules = useMemo(
    () => [...rules].sort((a, b) => (a.priority - b.priority) || a.name.localeCompare(b.name)),
    [rules]
  );

  const openCreate = () => { setEditing(null); setEditOpen(true); };
  const openCreateForCategory = (categoryId) => {
    // Phase 2Z — Setup → Rule deep-link entry point. Pre-fills the
    // category so the manager lands on a one-click "create rule for
    // <category>" experience instead of an empty form they have to
    // configure from scratch.
    setEditing({ category_id: categoryId });
    setEditOpen(true);
  };
  const openEdit = (r) => { setEditing(r); setEditOpen(true); };

  // Phase 2Z — accept a `?category_id=<id>&new=1` query param so the
  // Readiness/Setup "Open fix" buttons land on a pre-filled form. Only
  // runs once on mount.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (loading) return;
    const want = searchParams.get("new");
    const cid = searchParams.get("category_id");
    if (want === "1" && cid) {
      setFilterCat(cid);
      openCreateForCategory(cid);
      // Clear the params so a back/forward doesn't re-trigger the dialog.
      const next = new URLSearchParams(searchParams);
      next.delete("new"); next.delete("category_id");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
  const toggleActive = async (r) => {
    try {
      if (r.is_active) {
        await api.delete(`/assignment-rules/${r.rule_id}`);
        toast.success("Rule archived");
      } else {
        await api.patch(`/assignment-rules/${r.rule_id}`, { is_active: true });
        toast.success("Rule restored");
      }
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };
  const openPreview = (r) => { setPreviewRule(r); setPreviewOpen(true); };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyebrow">Setup</div>
          <h1 className="font-display text-4xl mt-1">Assignment Rules</h1>
          <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">
            Decide which employees can be assigned to which categories, including required
            skills, preferred skills, allowed combinations and per-employee limits.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <GenerateAssignmentRulesButton onDone={load} />
          <Button onClick={openCreate} data-testid="rules-create-btn"
            className="h-10 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
            <Plus className="h-4 w-4 mr-1" />New assignment rule
          </Button>
        </div>
      </div>

      <div className="mb-4 p-4 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE]"
        data-testid="rules-helper-card">
        <div className="text-sm font-semibold text-[#0B1A36] mb-1">What is an Assignment Rule?</div>
        <p className="text-xs text-[#1F2A44] leading-relaxed">
          An assignment rule sets the requirements for a particular type of work. For example,
          <em> &ldquo;Inbound calls require Spanish language&rdquo;</em> or <em>&ldquo;QA can only be done by
          someone certified in CompliancePro&rdquo;</em>. NextRota uses these to flag mis-assignments
          and, in future, to suggest the best person for each shift.
        </p>
      </div>

      <div className="flex items-start gap-2 p-3 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE] text-xs text-[#1F2A44] mb-5"
        data-testid="rules-disclaimer">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#2563EB]" />
        <div>
          Assignment rules guide who can be scheduled to which work. Managers
          always make the final call, and rules warn rather than block.
        </div>
      </div>

      <SetupHealthCard page="assignment" />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="h-10 rounded-lg w-56" data-testid="rules-filter-cat">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.filter((c) => c.is_active !== false).map((c) => (
              <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs text-[#4B5773]">
          <input type="checkbox" data-testid="rules-active-only"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
            className="accent-[#2563EB]" />
          Active only
        </label>
      </div>

      <Card>
        <table className="w-full text-sm" data-testid="rules-table">
          <thead>
            <tr className="text-left text-[#4B5773]">
              <th className="py-3 eyebrow font-normal w-16">Priority</th>
              <th className="py-3 eyebrow font-normal">Name</th>
              <th className="py-3 eyebrow font-normal">Category</th>
              <th className="py-3 eyebrow font-normal">Combinations</th>
              <th className="py-3 eyebrow font-normal">Required skills</th>
              <th className="py-3 eyebrow font-normal">Status</th>
              <th className="py-3 eyebrow font-normal text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={7} className="py-8 text-center text-[#4B5773]">Loading…</td></tr>
            )}
            {!loading && sortedRules.length === 0 && (
              <tr><td colSpan={7} className="py-12 text-center"
                      data-testid="rules-empty">
                <AssignmentRulesEmptyState onCreated={load} />
              </td></tr>
            )}
            {sortedRules.map((r) => (
              <tr key={r.rule_id} className={r.is_active ? "" : "opacity-50"}
                  data-testid={`rule-row-${r.rule_id}`}>
                <td className="py-3 font-mono text-sm">
                  <span data-testid={`rule-priority-${r.rule_id}`}
                    title="Priority order, lower numbers are evaluated first"
                    className="text-[11px] text-[#94A3B8]">P{r.priority}</span>
                </td>
                <td className="py-3">
                  <div className="font-medium text-[#0B1A36]">{r.name}</div>
                  {r.description && (
                    <div className="text-[11px] text-[#4B5773] truncate max-w-xs">
                      {r.description}
                    </div>
                  )}
                </td>
                <td className="py-3 text-[#4B5773]">{r.category_name || "-"}</td>
                <td className="py-3">
                  <Badge tone={COMBINE_TONE[r.combine_mode] || "slate"}>
                    {COMBINE_LABEL[r.combine_mode] || r.combine_mode}
                  </Badge>
                  {r.max_concurrent_for_employee != null && (
                    <div className="text-[11px] text-[#4B5773] mt-1">
                      Max {r.max_concurrent_for_employee} concurrent
                    </div>
                  )}
                  {(r.combine_category_names || []).length > 0 && (
                    <div className="text-[11px] text-[#4B5773] mt-1 truncate max-w-[180px]">
                      {(r.combine_category_names).join(", ")}
                    </div>
                  )}
                </td>
                <td className="py-3 text-xs text-[#4B5773]">
                  {(r.required_skills || []).length === 0 ? "-" : (
                    <span data-testid={`rule-req-skills-${r.rule_id}`}>
                      {(r.required_skills || []).length} required
                      {(r.preferred_skills || []).length > 0 && (
                        <span> · {(r.preferred_skills || []).length} preferred</span>
                      )}
                    </span>
                  )}
                </td>
                <td className="py-3">
                  {r.is_active
                    ? <Badge tone="green">Active</Badge>
                    : <Badge tone="slate">Archived</Badge>}
                </td>
                <td className="py-3 text-right space-x-1 whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => openPreview(r)}
                    data-testid={`rule-preview-${r.rule_id}`} title="Preview eligibility">
                    <Play className="h-4 w-4 text-[#2563EB]" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(r)}
                    data-testid={`rule-edit-${r.rule_id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleActive(r)}
                    data-testid={`rule-archive-${r.rule_id}`}>
                    {r.is_active ? <Archive className="h-4 w-4" /> : <ArchiveRestore className="h-4 w-4" />}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <RuleEditorDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        editing={editing}
        categories={categories}
        skills={skills}
        onSaved={() => { setEditOpen(false); load(); }}
      />
      <RulePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        rule={previewRule}
        skills={skills}
      />
    </div>
  );
}

function Badge({ tone = "slate", children, testid }) {
  const TONES = {
    green:  "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:  "bg-amber-50 text-amber-700 border-amber-200",
    red:    "bg-red-50 text-red-700 border-red-200",
    blue:   "bg-[#DBE7FE] text-[#1D4ED8] border-[#BFD3FB]",
    slate:  "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span data-testid={testid}
      className={`inline-flex items-center text-[11px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full border ${TONES[tone] || TONES.slate}`}>
      {children}
    </span>
  );
}

// Assignment Rules empty-state + Generate Recommended button.
// Mirrors the PoliciesPage pattern so the onboarding experience is
// consistent across all four setup-rule pages.

function AssignmentRulesEmptyState({ onCreated }) {
  return (
    <div className="max-w-2xl mx-auto py-4 text-left">
      <div className="rounded-lg border-2 border-dashed border-[#BFD3FB] bg-[#F0F6FF] p-6"
           data-testid="assignment-empty-card">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-5 w-5 text-[#2563EB]" />
          <h3 className="font-medium text-base text-[#0B1A36]">
            No assignment rules yet
          </h3>
        </div>
        <p className="text-sm text-[#1F2A44]">
          You can create assignment rules yourself, or let NextRota generate a
          recommended starter set based on your business. Existing items are
          never overwritten.
        </p>
        <div className="mt-4">
          <GenerateAssignmentRulesButton onDone={onCreated} prominent />
        </div>
      </div>

      <div className="mt-3 p-4 rounded-lg border border-slate-200 bg-white"
           data-testid="assignment-example">
        <div className="text-[10px] uppercase tracking-widest text-[#64748B] mb-1">
          Example assignment rule
        </div>
        <p className="text-sm text-[#0B1A36] font-medium">Admin desk cover</p>
        <div className="mt-2 grid grid-cols-2 gap-3 text-xs text-[#1F2A44]">
          <div>
            <div className="text-[#64748B] mb-1">Required skills</div>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Reception</li>
              <li>Phone handling</li>
            </ul>
          </div>
          <div>
            <div className="text-[#64748B] mb-1">Preferred skills</div>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Customer service</li>
            </ul>
          </div>
        </div>
        <p className="text-xs text-[#4B5773] mt-3">
          <strong>Why this matters:</strong> ensures only suitably trained
          employees are assigned to reception coverage. Required skills are
          hard filters, preferred skills inform the engine&apos;s ranking.
        </p>
      </div>
    </div>
  );
}

function GenerateAssignmentRulesButton({ onDone, prominent = false }) {
  const [busy, setBusy] = useState(false);
  async function run() {
    setBusy(true);
    try {
      const status = await api.get("/setup/wizard-status").catch(() => ({ data: {} }));
      const templateId = status.data?.recommended_template_id || "office_small";
      const { data } = await api.post("/setup/wizard-complete", {
        template_id: templateId,
        industry: status.data?.industry,
        size_band: status.data?.size_band,
        only_kinds: ["assignment"],
        dry_run: false,
      });
      const created = data.counts?.assignment || 0;
      if (created > 0) {
        toast.success(`Created ${created} recommended assignment rule${created === 1 ? "" : "s"}. Existing items were kept untouched.`);
      } else {
        toast("No new assignment rules created. Items with matching names already exist.");
      }
      onDone && onDone();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not generate rules");
    } finally { setBusy(false); }
  }
  return (
    <Button onClick={run} disabled={busy} size={prominent ? "default" : "sm"}
            variant={prominent ? "default" : "outline"}
            className={prominent
              ? "bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              : "h-10 border-[#BFD3FB] text-[#1D4ED8] hover:bg-[#F0F6FF]"}
            data-testid="rules-generate-btn">
      {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
            : <Sparkles className="h-3.5 w-3.5 mr-1" />}
      Generate recommended assignment rules
    </Button>
  );
}

